import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { selectedCustomer } from '@app/ngrx-customer/store';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { upsertMessage } from '@app/ngrx-message/store/actions/message/message.action';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OrderService } from '@order/services/order.service';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PATHS } from '@shared/constants/paths';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { productWithAlternativesSelector } from '@shared/selectors/product.selectors';
import { CART_ACTIONS, CART_SYNC_ACTIONS, OrderStatus } from '@usf/ngrx-order';
import { PRODUCT_PRICING_ACTIONS } from '@usf/ngrx-pricing';
import { ProductActions } from '@usf/ngrx-product';
import { EMPTY, combineLatest, of } from 'rxjs';
import {
  concatMap,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class CartEffects {
  deleteOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.deleteCartSuccess),
      tap(() => {
        const path = this.getCurrentPath();
        this.router.navigate([path.split('/')[1], 'my-orders-view']);
      }),
      switchMap(() => {
        return [
          upsertMessage({
            message: {
              id: this.uuid(),
              stack: null,
              watermark: new Date().toUTCString(),
              read: false,
              type: MessageTypeEnum.success,
              toast: true,
              display: 'Your order cancellation has been submitted',
            } as Message,
          }),
        ];
      }),
    ),
  );

  deleteOrderWithErrorDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.deleteCartWithErrorDetails),
      switchMap(() => {
        let actionsToExecute = [];
        const message = {
          id: this.uuid(),
          stack: null,
          watermark: new Date().toUTCString(),
          read: false,
          type: MessageTypeEnum.error,
          toast: true,
          display:
            'The order has been edited or submitted by another user, please refresh the page.',
        };
        actionsToExecute.push(upsertMessage({ message }));
        return actionsToExecute;
      }),
    ),
  );
  clientDeleteOrderFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.deleteCartFail),
      switchMap(error => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            toast: true,
            display: 'Cancel Order Failed',
          } as Message,
        }),
      ]),
    ),
  );

  clientSubmitCartSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.submitCartSuccess),
      mergeMap(action =>
        combineLatest([
          of(action),
          this.panamax.customer$,
          this.store.select(productWithAlternativesSelector),
        ]).pipe(take(1)),
      ),
      switchMap(([resp, customer, productsMap]) => {
        const actions: any[] = [];
        if (
          resp.order.orderHeader &&
          (resp.order.orderHeader.orderStatus === OrderStatus.SUBMITTING ||
            resp.order.orderHeader.orderStatus ===
              OrderStatus.EDITING_SUBMITTED) &&
          !resp.order.orderHeader.errorDetails
        ) {
          const expectedShipments = OrderService.calculateExpectedShipmentCount(
            resp.order,
            productsMap,
          );
          sessionStorage.setItem(
            'submitOrderExpectedShipments',
            expectedShipments?.toString(),
          );
          const orderId = `${resp.order.orderHeader.customerNumber}:${resp.order.orderHeader.departmentNumber}:${resp.order.orderHeader.orderId}`;
          this.navigationHelperService.routeTo(
            `${PATHS.ORDER_CONFIRMATION}/${orderId}`,
          );
        } else if (resp.order.orderHeader?.errorDetails?.errNumber === 10007) {
          this.navigationHelperService.routeTo(PATHS.MY_ORDERS_VIEW);
        } else if (resp.order.orderHeader?.errorDetails?.errNumber === 10008) {
          actions.push(
            upsertMessage({
              message: {
                id: this.uuid(),
                stack: null,
                watermark: new Date().toUTCString(),
                read: false,
                type: MessageTypeEnum.warning,
                display: this.translateService.instant(
                  'i18n.orderPage.invalidDateSubmitErrorMsg',
                ),
                dismissRoute: [PATHS.MY_ORDERS_VIEW],
              } as Message,
            }),
          );
          return actions;
        } else if (
          resp.order.orderHeader?.orderStatus === OrderStatus.CANCELLED
        ) {
          actions.push(
            upsertMessage({
              message: {
                id: this.uuid(),
                stack: null,
                watermark: new Date().toUTCString(),
                read: false,
                type: MessageTypeEnum.warning,
                toast: true,
                display: this.translateService.instant(
                  'i18n.orderPage.orderCancelledErrorMsg',
                ),
                dismissRoute: [PATHS.MY_ORDERS_VIEW],
              } as Message,
            }),
          );
          return actions;
        }
        if (!resp.order.orderHeader || !!resp.order.orderHeader.errorDetails) {
          actions.push(
            upsertMessage({
              message: {
                id: this.uuid(),
                stack: null,
                watermark: new Date().toUTCString(),
                read: false,
                type: MessageTypeEnum.warning,
                display:
                  'The order has been edited or submitted by another user, please review the order.',
                dismissRoute: [PATHS.MY_ORDERS_VIEW],
              } as Message,
            }),
          );
        }
        return actions;
      }),
    ),
  );

  loadProductsAndPricing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.loadCartIntoContext),
      map(payload => ({
        ...payload,
        uniqueProductNumbers: [
          ...new Set(
            payload.order?.orderItems?.map(
              orderItem => orderItem.productNumber,
            ),
          ),
        ],
      })),
      mergeMap(payload =>
        combineLatest([of(payload), this.store.select(selectedCustomer)]).pipe(
          take(1),
        ),
      ),
      concatMap(([payload, customer]) => {
        if (
          !payload.uniqueProductNumbers ||
          payload.uniqueProductNumbers.length < 1
        ) {
          return EMPTY;
        } else {
          const actions = [];
          actions.push(
            ProductActions.LoadProductsOnList({
              productIds: payload.uniqueProductNumbers,
            }),
          );

          if (customer.ogPrintPriceInd !== 'N') {
            actions.push(
              PRODUCT_PRICING_ACTIONS.loadProductPricing({
                productNumbers: payload.uniqueProductNumbers,
              }),
            );
          }
          return actions;
        }
      }),
    ),
  );

  cancelSubmittedCartSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.cancelSubmittedCartSuccess),
      tap(() => this.navigationHelperService.routeTo(PATHS.MY_ORDERS_VIEW)),
      switchMap(() => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.success,
            toast: true,
            display: 'Your order cancellation has been submitted',
          } as Message,
        }),
      ]),
    ),
  );

  cancelSubmittedCartFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.cancelSubmittedCartFail),
      switchMap(error => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            toast: true,
            display: 'Cancel Order Failed',
          } as Message,
        }),
      ]),
    ),
  );

  cancelSubmittedCartWithErrorDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.cancelSubmittedCartWithErrorDetails),
      switchMap(() => {
        let actionsToExecute = [];
        const message = {
          id: this.uuid(),
          stack: null,
          watermark: new Date().toUTCString(),
          read: false,
          type: MessageTypeEnum.warning,
          toast: true,
          display:
            'The order has been edited or submitted by another user, please refresh the page.',
        };
        actionsToExecute.push(upsertMessage({ message }));
        return actionsToExecute;
      }),
    ),
  );

  cartNotUpdatable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_SYNC_ACTIONS.cartNotUpdatable),
      switchMap(() => [
        upsertMessage({
          message: {
            id: this.uuid(),
            stack: null,
            watermark: new Date().toUTCString(),
            read: false,
            type: MessageTypeEnum.error,
            toast: false,
            title: 'Try Again',
            display:
              'There was a problem adding this to your order. Please try again.',
          },
        }),
      ]),
    ),
  );

  createCopiedOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.createCopiedOrderSuccess),
      tap(resp => {
        this.router.navigate([PATHS.ORDER]);
      }),
      switchMap(payload => {
        let actionsToExecute = [];
        const message = {
          id: this.uuid(),
          stack: null,
          watermark: new Date().toUTCString(),
          read: false,
          type: MessageTypeEnum.success,
          toast: true,
          display: `You've successfully copied order #${payload?.previousOrder?.orderHeader?.tandemOrderNumber}, it's loaded in your cart.`,
        } as Message;
        actionsToExecute.push(upsertMessage({ message }));
        return actionsToExecute;
      }),
    ),
  );

  createCopiedOrderFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CART_ACTIONS.createCopiedOrderFail),
      switchMap(error => {
        let actionsToExecute = [];
        const message = {
          id: this.uuid(),
          stack: null,
          watermark: new Date().toUTCString(),
          read: false,
          type: MessageTypeEnum.error,
          toast: true,
          display: `Something went wrong. Please try copying your order again.`,
        } as Message;
        actionsToExecute.push(upsertMessage({ message }));
        return actionsToExecute;
      }),
    ),
  );

  uuid() {
    return uuidv4();
  }
  getCurrentPath(): string {
    return window.location.pathname;
  }

  constructor(
    private actions$: Actions,
    private router: Router,
    private panamax: PanAppState,
    private store: Store,
    private navigationHelperService: NavigationHelperService,
    private translateService: TranslateService,
    readonly platformService: PlatformService,
  ) {}
}
